import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { App, Form, Input, InputNumber, Button, Space, Spin, Result, Divider } from 'antd';
import PortalLayout from './PortalLayout';
import PortalErrorPage from './PortalErrorPage';
import { portalApi, PortalInfoView } from '../../services/api/portalApi';

const PortalInfoPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loaded, setLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<'not_found' | 'expired' | 'rate_limited' | 'unknown' | null>(null);

  useEffect(() => {
    if (!token) return;
    portalApi.getInfo(token)
      .then((data: PortalInfoView) => {
        form.setFieldsValue({
          first_name: data.customer.first_name ?? '',
          last_name: data.customer.last_name ?? '',
          email: data.customer.email ?? '',
          phone: data.customer.phone ?? '',
          year: data.vehicle.year ?? null,
          make: data.vehicle.make ?? '',
          model: data.vehicle.model ?? '',
          vin: data.vehicle.vin ?? '',
          mileage: data.vehicle.mileage ?? null,
        });
        setLoaded(true);
      })
      .catch((err) => {
        const status = err?.response?.status;
        setError(status === 404 ? 'not_found' : status === 429 ? 'rate_limited' : 'unknown');
      });
  }, [token, form]);

  if (error) return <PortalErrorPage variant={error} />;

  if (done) {
    return (
      <PortalLayout title="" shopName="CNF Auto">
        <Result status="success" title="Thanks!" subTitle="We've received your info. The shop will follow up shortly." />
      </PortalLayout>
    );
  }

  if (!loaded) {
    return (
      <PortalLayout title="Loading…" shopName="CNF Auto">
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      </PortalLayout>
    );
  }

  const onFinish = async (values: any) => {
    if (!token) return;
    setSubmitting(true);
    try {
      await portalApi.submitInfo(token, {
        customer: {
          first_name: values.first_name, last_name: values.last_name,
          email: values.email, phone: values.phone,
        },
        vehicle: {
          year: values.year, make: values.make, model: values.model,
          vin: values.vin ? values.vin.toUpperCase() : undefined, mileage: values.mileage ?? undefined,
        },
      });
      setDone(true);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) setError('not_found');
      else message.error(err?.response?.data?.error || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout
      title="Quick info needed"
      shopName="CNF Auto"
      subtitle="Help us finish your estimate by filling in the details below."
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item label="First name" name="first_name" rules={[{ required: true, message: 'Required' }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Last name" name="last_name">
          <Input />
        </Form.Item>
        <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Invalid email' }]}>
          <Input type="email" inputMode="email" />
        </Form.Item>
        <Form.Item label="Phone" name="phone">
          <Input inputMode="tel" />
        </Form.Item>
        <Divider />
        <Space style={{ width: '100%' }} size="small" wrap>
          <Form.Item label="Year" name="year"><InputNumber min={1900} max={2100} /></Form.Item>
          <Form.Item label="Make" name="make"><Input /></Form.Item>
          <Form.Item label="Model" name="model"><Input /></Form.Item>
        </Space>
        <Form.Item
          label="VIN (17 chars)"
          name="vin"
          rules={[
            {
              validator: (_, v) => {
                if (!v) return Promise.resolve();
                if (/^[A-HJ-NPR-Z0-9]{17}$/.test(v.toUpperCase())) return Promise.resolve();
                return Promise.reject(new Error('VIN must be 17 chars, A-Z (no I/O/Q) + 0-9'));
              },
            },
          ]}
        >
          <Input maxLength={17} style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item label="Mileage" name="mileage">
          <InputNumber min={0} max={1000000} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>Submit</Button>
        </Form.Item>
      </Form>
    </PortalLayout>
  );
};

export default PortalInfoPage;
